import React from "react";
import PropTypes from "prop-types";
import withStyles from "react-jss";
import classNames from "classnames";
import { setDisplayName } from "recompose";

const styles = (theme) => ({
	root: {
		width: "100%",
		minHeight: "180px",
		padding: "27px 30px",
		borderRadius: "10px",
		border: `solid 1px ${theme.palette.colors.lightGrayishViolet}`,
		backgroundColor: theme.palette.colors.paleGrey,
		cursor: "pointer",
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		marginBottom: "20px",
	},
	checkout: {
		minHeight: "100%",
		padding: 0,
		border: "none",
		margin: 0,
		borderRadius: 0,
		backgroundColor: "transparent",
	},

	headerWrapper: {
		display: "flex",
		width: "100%",
		justifyContent: "space-between",
		alignItems: "flex-start",
		marginBottom: "18px",
	},
	checkoutHeaderWrapper: {
		marginBottom: 0,
	},
	header: {
		maxWidth: "230px",
		fontFamily: theme.fonts.bold,
		fontSize: "24px",
		lineHeight: "1",
		color: theme.palette.colors.darkSlateBlue,
	},
	checkoutHeader: {
		maxWidth: "100%",
		color: () => theme.palette.colors.white,
	},
	icon: {
		"& span": {
			maxWidth: "48px",
			maxHeight: "48px",
			color: theme.palette.colors.purplishBlue,
		},
	},
	text: {
		fontFamily: theme.fonts.light,
		fontSize: "16px",
		lineHeight: "1.25",
		color: theme.palette.colors.oneMoreGrey,
	},
	checkoutText: {
		color: () => theme.palette.colors.white,
	},
	yellowTheme: {
		"& $text": {
			color: "#cf0 !important",
		},
	},
	orange: {
		backgroundImage: "linear-gradient(295deg, #fbda61, #f7671c)",
		backgroundColor: "transparent",
		border: "none",
		"& $header": {
			color: theme.palette.colors.white,
		},
		"& $text": {
			color: theme.palette.colors.white,
		},
		"& $icon": {
			"& span": {
				maxWidth: "48px",
				maxHeight: "48px",
				color: theme.palette.colors.white,
			},
		},
	},
	purple: {
		backgroundColor: theme.palette.colors.purplishBlue,
		borderColor: "transparent",
		"& $header": {
			color: theme.palette.colors.white,
		},
		"& $text": {
			color: theme.palette.colors.white,
		},
		"& $icon": {
			"& span": {
				maxWidth: "48px",
				maxHeight: "48px",
				color: theme.palette.colors.white,
			},
		},
	},
	marginBottomZero: {
		marginBottom: 0,
	},
	[`@media (max-width: ${theme.media.sm})`]: {
		root: {
			width: "100%",
			minHeight: "110px",
			padding: "20px 8.33%",
			marginRight: "0",
			marginBottom: "0",
		},
		checkout: {
			minHeight: "100%",
			padding: 0,
		},
		headerWrapper: {
			marginBottom: "0",
			alignItems: "flex-start",
		},
		header: {
			maxWidth: "80%",
			fontSize: "18px",
		},
		checkoutText: {
			fontSize: "14px",
		},
		logo: {
			"& svg": {
				width: "32px",
				height: "32px",
			},
		},
	},
	[`@media (min-width: ${theme.media.md}) and (max-width: ${theme.media.lg})`]: {
		root: {
			width: "100%",
			minHeight: "220px",
		},
		checkout: {
			minHeight: "100%",
		},
		headerWrapper: {
			marginBottom: "14px",
		},
		checkoutHeaderWrapper: {
			marginBottom: 0,
		},
		header: {
			maxWidth: "170px",
		},
		checkoutHeader: {
			maxWidth: "100%",
		},
		checkoutText: {
			minHeight: "60px",
		},
	},
	[`@media (min-width: ${theme.media.sm}) and (max-width: ${theme.media.md})`]: {
		root: {
			width: "100%",
			minHeight: "190px",
			padding: "18px 20px",
			marginBottom: "10px",
		},
		checkout: {
			minHeight: "100%",
			padding: 0,
			marginBottom: 0,
		},
		headerWrapper: {
			marginBottom: "8px",
		},
		checkoutHeaderWrapper: {
			marginBottom: 0,
		},
		header: {
			maxWidth: "130px",
			fontSize: "18px",
		},
		checkoutHeader: {
			maxWidth: "100%",
		},
		text: {
			fontSize: "14px",
			lineHeight: "1.29",
		},
		checkoutText: {
			minHeight: "54px",
		},
	},
});

const Benefit = ({
	header,
	icon,
	text,
	backgroundColor,
	marginBottomZero,
	yellowTheme,
	classes,
	checkout,
	datatest,
}) => {
	const className = classNames(classes.root, {
		[classes.orange]: backgroundColor == "orange",
		[classes.purple]: backgroundColor == "purple",
		[classes.marginBottomZero]: marginBottomZero,
		[classes.yellowTheme]: yellowTheme,
		[classes.checkout]: checkout,
	});

	const classNameHeader = classNames(classes.header, {
		[classes.checkoutHeader]: checkout,
	});

	const classNameText = classNames(classes.text, {
		[classes.checkoutText]: checkout,
	});

	const classNameHeaderWrapper = classNames(classes.headerWrapper, {
		[classes.checkoutHeaderWrapper]: checkout,
	});

	return (
		<div className={className} datatest={datatest}>
			<div className={classNameHeaderWrapper}>
				<div className={classNameHeader}>{header}</div>
				<div className={classes.icon}>{icon}</div>
			</div>
			<div className={classNameText}>{text}</div>
		</div>
	);
};

Benefit.propTypes = {
	header: PropTypes.string.isRequired,
	text: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]).isRequired,
	backgroundColor: PropTypes.oneOf(["orange", "purple", ""]),
	icon: PropTypes.element,
	marginBottomZero: PropTypes.bool,
	yellowTheme: PropTypes.bool,
};

Benefit.defaultProps = {
	header: "",
	backgroundColor: "",
	icon: null,
	text: "",
	marginBottomZero: false,
	yellowTheme: false,
	checkout: false,
	datatest: null,
};
export default setDisplayName("BenefitDefault")(withStyles(styles)(Benefit));
